import React from 'react';

import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import Button from 'components/Button';
import { Dropdown, DropdownMenu, DropdownItem } from 'components/Dropdown2';
import Message from 'components/Message';
import { ICONS } from 'constants/icons';
import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';

import { SKU_ORDER_ACTION_SURFACE } from '../Items/SkuAnalytics.constants';
import SkuOrderActionMenuRow from '../shared/SkuOrderActionMenuRow';
import useEmailOrderCustomer from '../shared/useEmailOrderCustomer';
import messages from './SkuOrderDashboard.messages';
import flyoutMessages from '../shared/SkuOrderFlyout.messages';
import sharedMessages from '../shared/SkuOrder.messages';
import { LIFECYCLE_STATUS, SHOW_REPORT_USER_BUTTON } from './SkuOrders.constants';

const { element } = bem('sku-orders-dashboard');

const StatusPill = ({ message, withCheck = false, dark = false }) => (
  <span className={element('status-pill', { dark })}>
    {withCheck && <Isvg src={ICONS.checkmark} className={element('status-pill-icon')} />}
    <Message message={message} />
  </span>
);

StatusPill.propTypes = {
  message: intlMessage.isRequired,
  withCheck: PropTypes.bool,
  dark: PropTypes.bool,
};

const RowActionsIcon = () => (
  <span className={element('row-actions-icon')} aria-hidden="true">
    <span className={element('row-actions-dot')} />
    <span className={element('row-actions-dot')} />
    <span className={element('row-actions-dot')} />
  </span>
);

const STATUS_PILLS = {
  [LIFECYCLE_STATUS.CANCELLED]: <StatusPill message={sharedMessages.orderCancelled} />,
  [LIFECYCLE_STATUS.FULFILLED]: <StatusPill message={sharedMessages.fulfilled} withCheck dark />,
};

const OrderStatusCell = ({
  lifecycleStatus,
  showRowActions = true,
  onFulfill,
  isFulfilling = false,
  onEditItem,
  onCancelOrder,
  onCustomerMissedPickup,
  orderId,
  userName,
  userId,
  venueName,
  orderCreatedOn,
  isInTodayTab = false,
}) => {
  const intl = useIntl();
  const { openEmailCustomerModal } = useEmailOrderCustomer();
  const isActive = lifecycleStatus === LIFECYCLE_STATUS.ACTIVE;
  const isFulfilled = lifecycleStatus === LIFECYCLE_STATUS.FULFILLED;

  const handleContactCustomer = closeMenu => {
    closeMenu();
    openEmailCustomerModal({
      orderCreatedOn,
      venueName,
      userId,
      userName,
      orderId,
      surface: SKU_ORDER_ACTION_SURFACE.ACTIVE_ORDER_MENU,
    });
  };

  const content = isActive ? (
    <Button
      styled="primary"
      className={element('status-button')}
      onClick={e => {
        e.stopPropagation();
        onFulfill?.();
      }}
      disabled={isFulfilling}>
      <Message message={messages.markFulfilled} />
    </Button>
  ) : (
    STATUS_PILLS[lifecycleStatus]
  );

  if (!content) return null;

  return (
    <div className={element('status-cell', { 'with-actions': showRowActions })}>
      {content}
      {showRowActions && (
        <div role="presentation" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
          <Dropdown
            className={element('row-actions')}
            attachment="right"
            contentClassName={element('order-actions-dropdown')}
            isSmallRadiusStyle
            showMenuCaret={false}>
            {({ toggleMenu, closeMenu }) => (
              <>
                <div
                  role="button"
                  tabIndex={0}
                  className={element('row-actions-trigger')}
                  aria-label={intl.formatMessage(messages.orderActionsMenu)}
                  onClick={toggleMenu}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') toggleMenu(e);
                  }}>
                  <RowActionsIcon />
                </div>
                <DropdownMenu className={element('order-actions-menu')}>
                  {isActive && isInTodayTab && (
                    <DropdownItem onClick={() => handleContactCustomer(closeMenu)}>
                      <SkuOrderActionMenuRow iconSrc={ICONS.envelope} message={flyoutMessages.contactCustomer} />
                    </DropdownItem>
                  )}
                  <DropdownItem
                    onClick={() => {
                      closeMenu();
                      onEditItem?.();
                    }}>
                    <SkuOrderActionMenuRow iconSrc={ICONS.editPencilSku} message={messages.orderActionEditOrder} />
                  </DropdownItem>
                  {SHOW_REPORT_USER_BUTTON && (
                    <DropdownItem onClick={closeMenu}>
                      <SkuOrderActionMenuRow iconSrc={ICONS.flag} message={flyoutMessages.reportUser} />
                    </DropdownItem>
                  )}
                  {(isActive || isFulfilled) && isInTodayTab && (
                    <DropdownItem
                      onClick={() => {
                        closeMenu();
                        onCustomerMissedPickup?.();
                      }}>
                      <SkuOrderActionMenuRow
                        iconSrc={ICONS.userMissedPickup}
                        message={messages.orderActionCustomerMissedPickup}
                      />
                    </DropdownItem>
                  )}
                  {isActive && (
                    <DropdownItem
                      onClick={() => {
                        closeMenu();
                        onCancelOrder?.();
                      }}>
                      <SkuOrderActionMenuRow iconSrc={ICONS.cross} message={flyoutMessages.cancelOrder} destructive />
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </>
            )}
          </Dropdown>
        </div>
      )}
    </div>
  );
};

OrderStatusCell.propTypes = {
  lifecycleStatus: PropTypes.string.isRequired,
  showRowActions: PropTypes.bool,
  onFulfill: PropTypes.func,
  isFulfilling: PropTypes.bool,
  onEditItem: PropTypes.func,
  onCancelOrder: PropTypes.func,
  onCustomerMissedPickup: PropTypes.func,
  orderId: PropTypes.string,
  userName: PropTypes.string,
  userId: PropTypes.number,
  venueName: PropTypes.string,
  orderCreatedOn: PropTypes.string,
  isInTodayTab: PropTypes.bool,
};

export default OrderStatusCell;
